import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Save, Trash2, ShieldOff, Loader2, X, Upload, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const CATEGORIES = ["Small Chops", "Proteins", "Sides", "Platters"];
const BLANK = { id: "", name: "", category: "Small Chops", description: "", price: "", unit: "", image: "", sold_out: false, allergens: [], variants: [] };
const BLANK_VARIANT = { name: "", price: "", unit: "", note: "" };

export default function Admin() {
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [allergensVocab, setAllergensVocab] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create new; else existing item
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState({}); // {id: bool}
  const [uploading, setUploading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is over 8 MB — please pick a smaller one.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const loadMenu = async () => {
    setError("");
    try {
      const { data } = await api.get("/admin/menu");
      setItems(data.items);
      setAllergensVocab(data.allergens || []);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("Your Google account isn't set as an admin for this kitchen. Ask the developer to add your email to ADMIN_EMAILS.");
      } else if (err.response?.status === 401) {
        setError("Please sign in with Google first.");
      } else {
        setError("Could not load menu.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    loadMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const openCreate = () => { setEditing(null); setForm(BLANK); setDialogOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it, price: String(it.price), allergens: it.allergens || [], variants: (it.variants || []).map(v => ({ ...v, price: String(v.price) })) }); setDialogOpen(true); };

  const addVariant = () => setForm((f) => ({ ...f, variants: [...(f.variants || []), { ...BLANK_VARIANT }] }));
  const updateVariant = (i, patch) => setForm((f) => ({ ...f, variants: f.variants.map((v, idx) => idx === i ? { ...v, ...patch } : v) }));
  const removeVariant = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));

  const toggleAllergen = (key) => {
    setForm((f) => {
      const has = (f.allergens || []).includes(key);
      return { ...f, allergens: has ? f.allergens.filter((k) => k !== key) : [...(f.allergens || []), key] };
    });
  };

  const save = async () => {
    if (!form.name.trim() || !form.image.trim() || !form.description.trim() || !form.price) {
      toast.error("Fill name, description, price and image URL.");
      return;
    }
    setSaving(true);
    try {
      const cleanVariants = (form.variants || [])
        .filter(v => v.name.trim() && v.price)
        .map(v => ({ name: v.name.trim(), price: parseFloat(v.price), unit: v.unit || "", note: v.note || "" }));
      const payload = { ...form, price: parseFloat(form.price), variants: cleanVariants };
      if (editing) {
        const { data } = await api.put(`/admin/menu/${editing.id}`, payload);
        setItems((prev) => prev.map((p) => (p.id === editing.id ? { ...p, ...data } : p)));
        toast.success(`Updated ${data.name}`);
      } else {
        const { data } = await api.post("/admin/menu", payload);
        setItems((prev) => [...prev, data]);
        toast.success(`Added ${data.name}`);
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const toggleSoldOut = async (it, sold_out) => {
    setToggling((s) => ({ ...s, [it.id]: true }));
    try {
      await api.patch(`/admin/menu/${it.id}/sold-out`, { sold_out });
      setItems((prev) => prev.map((p) => (p.id === it.id ? { ...p, sold_out } : p)));
      toast.success(`${it.name} — ${sold_out ? "marked sold out" : "back on the menu"}`);
    } catch {
      toast.error("Could not update availability.");
    } finally {
      setToggling((s) => ({ ...s, [it.id]: false }));
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"? Customers won't see it anymore.`)) return;
    try {
      await api.delete(`/admin/menu/${it.id}`);
      setItems((prev) => prev.filter((p) => p.id !== it.id));
      toast.success(`Deleted ${it.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete.");
    }
  };

  // Gate: not signed in
  if (authLoading || loading) {
    return (
      <main className="max-w-5xl mx-auto px-6 py-24 text-center">
        <Loader2 className="w-6 h-6 mx-auto animate-spin text-[#1A6B2E]" />
      </main>
    );
  }
  if (!user) {
    return (
      <main data-testid="admin-signin-gate" className="max-w-md mx-auto px-6 py-24 text-center">
        <ShieldOff className="w-10 h-10 mx-auto text-[#D02030]" />
        <h1 className="font-display text-4xl mt-4 tracking-tighter">Admin sign in</h1>
        <p className="text-[#4E5C4F] mt-2">Sign in with the Google account registered as owner.</p>
        <button
          data-testid="admin-google-btn"
          onClick={login}
          className="btn-primary rounded-full px-6 py-3 mt-8 text-sm font-semibold"
        >
          Sign in with Google
        </button>
      </main>
    );
  }
  if (error) {
    return (
      <main data-testid="admin-forbidden" className="max-w-md mx-auto px-6 py-24 text-center">
        <ShieldOff className="w-10 h-10 mx-auto text-[#D02030]" />
        <h1 className="font-display text-3xl mt-4 tracking-tighter">Not authorised</h1>
        <p className="text-[#4E5C4F] mt-2">{error}</p>
        <p className="text-xs text-[#4E5C4F] mt-3">Signed in as <span className="font-mono">{user.email}</span></p>
        <Link to="/" className="inline-block mt-6 text-sm underline">← Back to homepage</Link>
      </main>
    );
  }

  const byCategory = CATEGORIES.map((c) => ({
    category: c,
    items: items.filter((i) => i.category === c),
  }));

  const persistOrder = async (nextItems) => {
    try {
      await api.patch("/admin/menu/reorder", { order: nextItems.map((i) => i.id) });
    } catch {
      toast.error("Could not save the new order — reload to try again.");
      loadMenu();
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeItem = items.find((i) => i.id === active.id);
    const overItem = items.find((i) => i.id === over.id);
    if (!activeItem || !overItem || activeItem.category !== overItem.category) return;

    // Reorder within the shared category, keeping other items in place
    const catItems = items.filter((i) => i.category === activeItem.category);
    const oldIdx = catItems.findIndex((i) => i.id === active.id);
    const newIdx = catItems.findIndex((i) => i.id === over.id);
    const reordered = arrayMove(catItems, oldIdx, newIdx);

    // Rebuild the full items list: for each category, use its (possibly new) sequence
    const nextByCat = new Map();
    for (const c of CATEGORIES) {
      nextByCat.set(c, c === activeItem.category
        ? reordered
        : items.filter((i) => i.category === c));
    }
    const nextItems = CATEGORIES.flatMap((c) => nextByCat.get(c));
    setItems(nextItems);
    persistOrder(nextItems);
  };

  return (
    <main data-testid="admin-page" className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <span className="font-label text-xs text-[#D02030]">Kitchen · Admin</span>
          <h1 className="font-display text-4xl md:text-6xl mt-3 tracking-tighter">Manage the menu.</h1>
          <p className="text-sm text-[#4E5C4F] mt-2">
            Add dishes, tweak prices, mark items sold out. Drag the <GripVertical className="w-3 h-3 inline align-baseline" /> handle to reorder within a category — the customer menu updates instantly.
          </p>
        </div>
        <div className="flex items-center gap-3 self-start">
          <Link
            to="/admin/orders"
            data-testid="admin-orders-link"
            className="text-sm font-medium rounded-full border border-[#1A6B2E]/25 px-4 py-2 hover:bg-[#F4EFEB]"
          >
            View orders →
          </Link>
          <button
            data-testid="add-item-btn"
            onClick={openCreate}
            className="btn-primary rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Add new item
          </button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="space-y-8">
          {byCategory.map(({ category, items: rows }) => (
            <section key={category}>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-display text-2xl">{category}</h2>
                <span className="text-xs text-[#4E5C4F]">{rows.length} item{rows.length === 1 ? "" : "s"}</span>
              </div>
              {rows.length === 0 ? (
                <div className="chop-card p-6 text-sm text-[#4E5C4F]">No items in this category yet.</div>
              ) : (
                <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {rows.map((it) => (
                      <SortableRow
                        key={it.id}
                        item={it}
                        toggling={!!toggling[it.id]}
                        onEdit={() => openEdit(it)}
                        onDelete={() => remove(it)}
                        onSoldOutChange={(v) => toggleSoldOut(it, v)}
                      />
                    ))}
                  </div>
                </SortableContext>
              )}
            </section>
          ))}
        </div>
      </DndContext>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="menu-item-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tighter">
              {editing ? `Edit — ${editing.name}` : "Add a new item"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input data-testid="form-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Chin Chin" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Price (CAD)</Label>
                <Input data-testid="form-price" type="number" step="0.50" min="0" value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="20.00" />
              </div>
            </div>
            <div>
              <Label>Unit / Serving (optional)</Label>
              <Input data-testid="form-unit" value={form.unit || ""}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                placeholder="e.g. 24 pieces, serves 3" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea data-testid="form-description" rows={3} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Crispy, spiced, party-ready…" />
            </div>
            <div>
              <Label>Image</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input data-testid="form-image" value={form.image}
                  onChange={(e) => setForm({ ...form, image: e.target.value })}
                  placeholder="Upload a photo or paste a URL/path"
                  className="flex-1" />
                <label
                  data-testid="upload-image-btn"
                  className="cursor-pointer rounded-full border border-[#1A6B2E]/25 px-4 py-2 text-xs font-medium hover:bg-[#F4EFEB] inline-flex items-center gap-2 whitespace-nowrap"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? "Uploading…" : "Upload"}
                  <input
                    data-testid="upload-image-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f); e.target.value = ""; }}
                  />
                </label>
              </div>
              {form.image && (
                <img src={form.image} alt="preview"
                  className="mt-2 w-28 h-28 rounded-lg object-cover border border-[#1A6B2E]/15" />
              )}
              <p className="mt-1 text-[10px] text-[#4E5C4F]">PNG · JPG · WEBP up to 8 MB. Uploaded photos are hosted on Chop Box.</p>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Switch data-testid="form-sold-out" checked={form.sold_out}
                onCheckedChange={(v) => setForm({ ...form, sold_out: v })} />
              <span className="text-sm">Mark as sold out</span>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>Sizes / variants</Label>
                  <p className="text-[10px] text-[#4E5C4F] mt-0.5">Leave empty for a single fixed price. Add sizes for a picker.</p>
                </div>
                <button
                  type="button"
                  data-testid="add-variant-btn"
                  onClick={addVariant}
                  className="text-xs rounded-full border border-[#1A6B2E]/25 px-3 py-1 hover:bg-[#F4EFEB] inline-flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add size
                </button>
              </div>
              {(form.variants || []).length > 0 && (
                <div className="mt-2 space-y-2">
                  {form.variants.map((v, i) => (
                    <div key={i} data-testid={`variant-row-${i}`} className="rounded-lg border border-[#1A6B2E]/15 p-2 bg-[#F4EFEB]/40">
                      <div className="grid grid-cols-12 gap-2">
                        <Input
                          data-testid={`variant-name-${i}`}
                          value={v.name}
                          onChange={(e) => updateVariant(i, { name: e.target.value })}
                          placeholder="Size name (e.g. 12 pieces)"
                          className="col-span-6 h-9 text-sm"
                        />
                        <Input
                          data-testid={`variant-price-${i}`}
                          type="number" step="0.50" min="0"
                          value={v.price}
                          onChange={(e) => updateVariant(i, { price: e.target.value })}
                          placeholder="Price"
                          className="col-span-3 h-9 text-sm"
                        />
                        <Input
                          data-testid={`variant-unit-${i}`}
                          value={v.unit || ""}
                          onChange={(e) => updateVariant(i, { unit: e.target.value })}
                          placeholder="Unit"
                          className="col-span-2 h-9 text-sm"
                        />
                        <button
                          type="button"
                          data-testid={`variant-remove-${i}`}
                          onClick={() => removeVariant(i)}
                          className="col-span-1 text-[#D02030] hover:bg-[#D02030]/10 rounded-full grid place-items-center"
                          aria-label="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <Input
                        data-testid={`variant-note-${i}`}
                        value={v.note || ""}
                        onChange={(e) => updateVariant(i, { note: e.target.value })}
                        placeholder="Extra note (optional) — e.g. includes 1L plantain"
                        className="mt-2 h-8 text-xs"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label>Allergens & dietary tags</Label>
              <p className="text-[10px] text-[#4E5C4F] mt-0.5 mb-2">
                Shown as pills on the customer menu — helps guests order safely.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {allergensVocab.map((tag) => {
                  const active = (form.allergens || []).includes(tag.key);
                  const isDietary = tag.kind === "dietary";
                  return (
                    <button
                      key={tag.key}
                      type="button"
                      data-testid={`form-allergen-${tag.key}`}
                      onClick={() => toggleAllergen(tag.key)}
                      className={`rounded-full px-3 py-1 text-xs font-label border transition-colors ${
                        active
                          ? isDietary
                            ? "bg-[#1A6B2E] text-white border-[#1A6B2E]"
                            : "bg-[#D02030] text-white border-[#D02030]"
                          : "bg-white text-[#4E5C4F] border-[#1A6B2E]/25 hover:bg-[#F4EFEB]"
                      }`}
                    >
                      {tag.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <button
              data-testid="save-item-btn"
              onClick={save}
              disabled={saving}
              className="btn-primary rounded-full px-5 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {editing ? "Save changes" : "Add to menu"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}


function SortableRow({ item, toggling, onEdit, onDelete, onSoldOutChange }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 40 : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`admin-row-${item.id}`}
      className={`chop-card chop-shadow p-4 flex gap-3 items-center transition-opacity ${item.sold_out ? "opacity-70" : ""} ${isDragging ? "shadow-2xl" : ""}`}
    >
      <button
        type="button"
        data-testid={`drag-handle-${item.id}`}
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
        className="text-[#4E5C4F] hover:text-[#1A6B2E] cursor-grab active:cursor-grabbing p-1 -ml-1 touch-none"
      >
        <GripVertical className="w-5 h-5" />
      </button>
      <img src={item.image} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0 border border-[#1A6B2E]/10" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-display text-lg truncate">{item.name}</p>
          <p className="font-display text-lg text-[#D02030]">${item.price.toFixed(2)}</p>
        </div>
        <p className="text-xs text-[#4E5C4F] mt-0.5">{item.unit || "—"}</p>
        <div className="mt-3 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Switch
              data-testid={`sold-out-toggle-${item.id}`}
              checked={!!item.sold_out}
              disabled={toggling}
              onCheckedChange={onSoldOutChange}
            />
            <span className={`text-xs font-label ${item.sold_out ? "text-[#D02030]" : "text-[#1A6B2E]"}`}>
              {item.sold_out ? "Sold out" : "Available"}
            </span>
          </div>
          <button
            data-testid={`edit-btn-${item.id}`}
            onClick={onEdit}
            className="text-xs rounded-full border border-[#1A6B2E]/25 px-3 py-1 hover:bg-[#F4EFEB]"
          >
            Edit
          </button>
          <button
            data-testid={`delete-btn-${item.id}`}
            onClick={onDelete}
            className="text-xs text-[#D02030] hover:underline inline-flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}
